import { Warning } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';

/** Painel: uma única moldura, sem cards aninhados. */
export function Panel({ title, id, subtitle, actions, children, className, testId }: { title: string; id: string; subtitle?: string; actions?: ReactNode; children: ReactNode; className?: string; testId?: string }) {
  return (
    <section aria-labelledby={id} data-testid={testId} className={cn('min-w-0 rounded-panel border bg-card', className)}>
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-4 pt-4 sm:px-5">
        <div className="min-w-0">
          <h2 id={id} className="font-display text-base font-semibold sm:text-lg">
            {title}
          </h2>
          {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        {actions}
      </header>
      <div className="px-4 pt-3 pb-4 sm:px-5 sm:pb-5">{children}</div>
    </section>
  );
}

export function InlineEmpty({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('rounded-control bg-muted/60 px-3 py-3 text-sm text-muted-foreground', className)}>{children}</p>;
}

export function InlineError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control bg-destructive-soft px-3 py-3 text-sm text-foreground">
      <Warning className="size-5 shrink-0 text-destructive" weight="fill" aria-hidden />
      <span className="min-w-0 flex-1">{message}</span>
      <Button variant="secondary" size="md" onClick={onRetry}>
        Tentar de novo
      </Button>
    </div>
  );
}

/** Tooltip por hover e foco de teclado; o conteúdo também vai no aria-label do alvo. */
export function Tip({ children, align = 'start' }: { children: ReactNode; align?: 'start' | 'end' }) {
  return (
    <span
      role="tooltip"
      className={cn(
        'pointer-events-none invisible absolute bottom-full z-20 mb-1.5 w-max max-w-[14rem] rounded-control border bg-card px-2.5 py-1.5 text-xs leading-snug text-foreground opacity-0 shadow-[0_8px_24px_-10px_oklch(0.25_0.04_205/0.4)] group-hover/tip:visible group-hover/tip:opacity-100 group-focus-visible/tip:visible group-focus-visible/tip:opacity-100',
        align === 'start' ? 'left-0' : 'right-0',
      )}
    >
      {children}
    </span>
  );
}

export type BarTone = 'bar' | 'overdue' | 'neutral' | 'received' | 'pending';
const TONE: Record<BarTone, string> = {
  bar: 'bg-viz-bar',
  overdue: 'bg-viz-overdue',
  received: 'bg-viz-received',
  pending: 'bg-viz-pending',
  neutral: 'bg-muted-foreground',
};
export const toneClass = (t: BarTone) => TONE[t];

export interface BarRowProps {
  label: string;
  /** texto principal à direita (valor) */
  valueText: string;
  /** texto secundário à direita (ex.: percentual) */
  detail?: string;
  /** 0..1, relativo ao maior valor; o eixo sempre começa em zero */
  scale: number;
  tone?: BarTone;
  /** frase completa do tooltip e do aria-label */
  title: string;
  zero?: boolean;
}

/** Linha de barra horizontal com rótulo direto e valor. Alvo focável para o tooltip. */
export function BarRow({ label, valueText, detail, scale, tone = 'bar', title, zero }: BarRowProps) {
  const width = scale > 0 ? Math.max(scale * 100, 1.5) : 0;
  return (
    <div
      tabIndex={0}
      role="img"
      aria-label={title}
      className="group/tip relative grid min-h-10 grid-cols-[minmax(0,7.25rem)_minmax(0,1fr)_auto] items-center gap-x-3 rounded-control px-1 hover:bg-muted/60 focus-visible:bg-muted/60 sm:grid-cols-[9rem_minmax(0,1fr)_auto]"
    >
      <span className={cn('truncate text-sm', zero && 'text-muted-foreground')}>{label}</span>
      <span className="relative block h-3" aria-hidden>
        <span className="absolute inset-y-0 left-0 w-px bg-border" />
        <span
          className={cn('absolute inset-y-0 left-px rounded-r-[4px] motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-out', TONE[tone])}
          style={{ width: `calc(${width}% - 1px)` }}
        />
      </span>
      <span className="flex min-w-[4.25rem] items-baseline justify-end gap-1.5 text-right tabular" aria-hidden>
        <span className={cn('text-sm font-semibold', zero && 'font-normal text-muted-foreground')}>{valueText}</span>
        {detail && <span className="w-11 text-xs text-muted-foreground">{detail}</span>}
      </span>
      <Tip>{title}</Tip>
    </div>
  );
}

export function PanelSkeleton({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('rounded-panel border bg-card p-4 sm:p-5', className)} aria-hidden>
      <Skeleton className="mb-4 h-5 w-40" />
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className="h-4" />
        ))}
      </div>
    </div>
  );
}
